import { useI18n } from "@baton/i18n";
import { X } from "@baton/icons";
import { cva } from "class-variance-authority";
import { Dialog as Primitive } from "radix-ui";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { IconButton } from "../../primitives/buttons/IconButton/IconButton.tsx";
import { useScrollFade } from "../../primitives/layout/ScrollArea/scrollFade.ts";
import { AlertDialog } from "../AlertDialog/AlertDialog.tsx";
import { PortalContainerContext, useOverlayStage, usePortalHost } from "../portalContainer.tsx";
import { useReturnFocus } from "../returnFocus.ts";
import { dialogDescriptionClassName, dialogTitleClassName, scrimClassName } from "../surfaces.ts";

/**
 * FR-015-101, § Overlays, Dialog, taken from Spectrum UI's Responsive Modal
 * (`@spectrumui/responsive-modal-dependencies`, Apache-2.0) over Radix and themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision): a sheet rising from the
 * bottom edge at narrow widths and a centred panel from `--bp-md` up, in the four sizes, with the
 * footer, the unsaved-changes confirmation on dismiss, and focus moved in, trapped and restored.
 *
 * What differs from the source, and why:
 * - the panel becomes centred at `--bp-md` (768 px), where the source waits for 1024 px, because
 *   Baton's layouts change at `--bp-md` and a tablet in portrait keeps the centred panel;
 * - the centred panel scales in without the source's slide from 48 percent, which is a raw offset
 *   the tokens do not hold, and the sheet's height stops `--space-10` short of the top where the
 *   source stops at 90 percent;
 * - the sizes are `--dialog-width-sm`, `-md` and `-lg`; `xl` is `--dialog-width-xl` by
 *   `--dialog-height-xl`, each capped at `--dialog-xl-share` of the viewport (2026-09-18, by the
 *   owner's decision, so the item dialog takes most of the screen), for a surface with two halves
 *   such as the item dialog; and `full` fills the viewport inside that margin and becomes the
 *   whole screen at narrow widths, where the source has one 32 rem size;
 * - the interactive floating panels opened inside the dialog portal into its content
 *   (`portalContainer.tsx`), so the modal dialog's scroll lock leaves them scrollable, which the
 *   source's body portal does not; a tooltip stays in the body, over the dialog on the tooltip layer;
 * - the body scrolls between a fixed header and footer, where the source scrolls the whole panel,
 *   so Save and Cancel stay in view in a long form;
 * - the close control is Baton's IconButton with its translated name, where the source writes an
 *   English screen-reader label.
 */
export type DialogSize = "sm" | "md" | "lg" | "xl" | "full";

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly size?: DialogSize;
  readonly footer?: ReactNode;
  /** Controls at the end of the title row, before the close button (an item dialog's presentation switcher). */
  readonly actions?: ReactNode;
  readonly dismissible?: boolean;
  /** True while the dialog holds unsaved edits: a dismiss then asks for confirmation. */
  readonly unsavedChanges?: boolean;
  readonly initialFocus?: React.RefObject<HTMLElement | null>;
  /**
   * True while a control inside the dialog is taking `Escape` for itself, so the dialog keeps its own
   * (2026-09-21, the New task dialog's `@` completion). The library reads `Escape` in the capture phase,
   * ahead of the control's own handler, so the host has to say so here rather than stop the event.
   */
  readonly escapeGuard?: () => boolean;
  /**
   * Where focus goes when the dialog closes. Radix returns it to the dialog's trigger, and a dialog
   * opened with no trigger, from a row's `Enter` or a route change, would leave it on the document
   * body; a host that knows the element the dialog came from focuses it here and prevents the default.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
  /** Every key pressed in the panel, after the controls inside it had theirs (the item dialog's previous and next, 2026-10-03). */
  readonly onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  readonly trigger?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

const panel = cva(
  cn(
    "fixed inset-x-0 bottom-0 z-(--layer-overlay) flex max-h-[calc(100%_-_var(--space-10))] flex-col rounded-t-xl border-t bg-background shadow-lg outline-hidden transition ease-in-out data-[state=open]:animate-in data-[state=open]:duration-(--duration-slow) data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-base) data-[state=closed]:slide-out-to-bottom",
    "md:inset-x-auto md:bottom-auto md:top-1/2 md:left-1/2 md:w-full md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-xl md:border md:data-[state=open]:slide-in-from-bottom-0 md:data-[state=open]:fade-in-0 md:data-[state=open]:zoom-in-95 md:data-[state=closed]:slide-out-to-bottom-0 md:data-[state=closed]:fade-out-0 md:data-[state=closed]:zoom-out-95",
  ),
  {
    variants: {
      size: {
        sm: "md:max-h-[calc(100%_-_var(--space-6)_*_2)] md:max-w-(--dialog-width-sm)",
        md: "md:max-h-[calc(100%_-_var(--space-6)_*_2)] md:max-w-(--dialog-width-md)",
        lg: "md:max-h-[calc(100%_-_var(--space-6)_*_2)] md:max-w-(--dialog-width-lg)",
        xl: "md:h-[min(var(--dialog-height-xl),var(--dialog-xl-share))] md:max-h-none md:max-w-[min(var(--dialog-width-xl),var(--dialog-xl-share))]",
        full: "top-0 max-h-none rounded-none border-t-0 md:h-[calc(100%_-_var(--space-6)_*_2)] md:max-h-none md:max-w-[calc(100%_-_var(--space-6)_*_2)]",
      },
    },
  },
);

export function Dialog({ open, onOpenChange, title, description, size = "md", footer, actions, dismissible = true, unsavedChanges = false, initialFocus, escapeGuard, onCloseAutoFocus, onKeyDown, trigger, children, className }: DialogProps) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  // The body fades at an edge its content continues past (`scrollFade.ts`, 2026-10-21).
  const body = useScrollFade<HTMLDivElement>();
  useReturnFocus(open);
  // The content element the interactive floating panels opened inside this dialog portal into.
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const host = usePortalHost(content, true);
  const overlayStage = useOverlayStage();
  const requestClose = () => {
    if (!dismissible) return;
    if (unsavedChanges) setConfirming(true);
    else onOpenChange(false);
  };
  return (
    <Primitive.Root
      data-slot="dialog"
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else requestClose();
      }}
    >
      {trigger !== undefined && (
        <Primitive.Trigger data-slot="dialog-trigger" asChild>
          {trigger}
        </Primitive.Trigger>
      )}
      <Primitive.Portal data-slot="dialog-portal" {...(overlayStage === undefined ? {} : { container: overlayStage })}>
        <Primitive.Overlay data-slot="dialog-overlay" className={scrimClassName} />
        <Primitive.Content
          ref={setContent}
          data-slot="dialog-content"
          className={cn(panel({ size }), className)}
          data-size={size}
          onOpenAutoFocus={(event) => {
            // With no field named to take it, the focus goes to the panel itself rather than to the
            // first control, the close button, whose tooltip would open with the dialog and take the
            // first Esc for itself (2026-09-28); Tab from the panel reaches the close button.
            event.preventDefault();
            const panelElement = event.target instanceof HTMLElement ? event.target : null;
            (initialFocus?.current ?? panelElement)?.focus();
          }}
          {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
          {...(onKeyDown === undefined ? {} : { onKeyDown })}
          onEscapeKeyDown={(event) => {
            if (escapeGuard?.() === true) {
              event.preventDefault();
              return;
            }
            if (!dismissible || unsavedChanges) event.preventDefault();
            if (unsavedChanges && dismissible) setConfirming(true);
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible || unsavedChanges) event.preventDefault();
            if (unsavedChanges && dismissible) setConfirming(true);
          }}
        >
          <PortalContainerContext.Provider value={host}>
            <div data-slot="dialog-header" className="flex flex-col gap-2 px-6 pt-6 text-center sm:text-start">
              <div className="flex items-center gap-3">
                <Primitive.Title data-slot="dialog-title" className={cn(dialogTitleClassName, "min-w-0 flex-1")}>
                  {title}
                </Primitive.Title>
                {actions !== undefined && <span className="inline-flex flex-none items-center gap-1">{actions}</span>}
                {dismissible && (
                  <Primitive.Close data-slot="dialog-close" asChild>
                    <IconButton icon={<X />} label={t("ui.dialog.close")} size="sm" className="ms-2" />
                  </Primitive.Close>
                )}
              </div>
              {description !== undefined ? (
                <Primitive.Description data-slot="dialog-description" className={dialogDescriptionClassName}>
                  {description}
                </Primitive.Description>
              ) : null}
            </div>
            <div ref={body} data-slot="dialog-body" className="min-h-0 flex-1 overflow-y-auto px-6 py-4 last:pb-6">
              {children}
            </div>
            {footer !== undefined && (
              <div data-slot="dialog-footer" className="flex flex-col-reverse gap-2 px-6 pb-6 sm:flex-row sm:justify-end">
                {footer}
              </div>
            )}
          </PortalContainerContext.Provider>
        </Primitive.Content>
      </Primitive.Portal>
      {unsavedChanges && (
        <AlertDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={t("ui.dialog.unsaved.title")}
          body={t("ui.dialog.unsaved.body")}
          confirmLabel={t("ui.dialog.unsaved.discard")}
          confirmVariant="danger"
          onConfirm={() => {
            setConfirming(false);
            onOpenChange(false);
          }}
        />
      )}
    </Primitive.Root>
  );
}
